/**
 * Available merge tags for email templates
 */
export const MERGE_TAGS = [
  { name: 'First Name', value: '{{firstName}}', sample: 'John' },
  { name: 'Last Name', value: '{{lastName}}', sample: 'Doe' },
  { name: 'Email', value: '{{email}}', sample: 'john.doe@example.com' },
  { name: 'Full Name', value: '{{fullName}}', sample: 'John Doe' },
  { name: 'Company Name', value: '{{companyName}}', sample: 'Acme Corp' },
  { name: 'Account Name', value: '{{accountName}}', sample: 'Acme Corp' },
  { name: 'Projects Created', value: '{{projectsCreated}}', sample: '5' },
  { name: 'Timesheets Created', value: '{{timesheetsCreated}}', sample: '150' },
  { name: 'Days Since Last Activity', value: '{{daysSinceLastActivity}}', sample: '3' }
];

/**
 * Extract all merge tags from a string
 */
export function extractMergeTags(text: string): string[] {
  const regex = /\{\{(\w+)\}\}/g;
  const matches: string[] = [];
  let match;
  while ((match = regex.exec(text)) !== null) {
    if (!matches.includes(match[0])) {
      matches.push(match[0]);
    }
  }
  return matches;
}

/**
 * Replace merge tags in text with sample values (for preview)
 */
export function replaceMergeTagsWithSamples(text: string): string {
  let result = text;
  MERGE_TAGS.forEach(tag => {
    result = result.replace(new RegExp(tag.value.replace(/[{}]/g, '\\$&'), 'g'), tag.sample);
  });
  return result;
}

/**
 * Insert a merge tag at cursor position in a textarea/input
 */
export function insertMergeTag(element: HTMLTextAreaElement | HTMLInputElement, tag: string): void {
  const start = element.selectionStart || 0;
  const end = element.selectionEnd || 0;
  const text = element.value;
  const before = text.substring(0, start);
  const after = text.substring(end);
  
  element.value = before + tag + after;
  element.selectionStart = element.selectionEnd = start + tag.length;
  element.focus();
  
  // Trigger input event for React controlled components
  const event = new Event('input', { bubbles: true });
  element.dispatchEvent(event);
}

/**
 * Check if text contains merge tags
 */
export function hasMergeTags(text: string): boolean {
  return /\{\{\w+\}\}/.test(text);
}

/**
 * Split text into parts (text and merge tag matches)
 * Used for rendering badges
 */
export function splitTextWithMergeTags(text: string): Array<{ type: 'text' | 'tag'; content: string; tagName?: string }> {
  if (!text) return [];
  
  const parts: Array<{ type: 'text' | 'tag'; content: string; tagName?: string }> = [];
  const regex = /\{\{(\w+)\}\}/g;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      const textPart = text.substring(lastIndex, match.index);
      if (textPart) {
        parts.push({ type: 'text', content: textPart });
      }
    }

    // Add merge tag
    parts.push({
      type: 'tag',
      content: match[0],
      tagName: match[1]
    });

    lastIndex = regex.lastIndex;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    const remainingText = text.substring(lastIndex);
    if (remainingText) {
      parts.push({ type: 'text', content: remainingText });
    }
  }

  // If no matches, return the original text
  if (parts.length === 0) {
    return [{ type: 'text', content: text }];
  }

  return parts;
}
